import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const QuotationList = ({ quotations, selectedQuotation, onSelectQuotation, onCreateNew }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const statusConfig = {
    draft: { label: 'Borrador', color: 'bg-secondary-100 text-secondary-600', icon: 'FileText' },
    sent: { label: 'Enviado', color: 'bg-primary-100 text-primary-600', icon: 'Send' },
    approved: { label: 'Aprobado', color: 'bg-success-100 text-success-600', icon: 'CheckCircle' },
    expired: { label: 'Expirado', color: 'bg-error-100 text-error-600', icon: 'Clock' }
  };

  const filteredQuotations = quotations.filter(quotation => {
    const matchesSearch = quotation.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quotation.number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || quotation.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('es-ES').format(new Date(date));
  };

  return (
    <div className="bg-surface rounded-lg border border-border h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold text-text-primary">
            Presupuestos
          </h2>
          <Button
            variant="primary"
            size="sm"
            iconName="Plus"
            onClick={onCreateNew}
          >
            Nuevo
          </Button>
        </div>

        {/* Search */}
        <div className="mb-3">
          <Input
            type="search"
            placeholder="Buscar por cliente o número..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-micro ${
              statusFilter === 'all' ?'bg-primary text-primary-foreground' :'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
            }`}
          >
            Todos
          </button>
          {Object.entries(statusConfig).map(([status, config]) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-micro ${
                statusFilter === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
              }`}
            >
              {config.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quotation List */}
      <div className="flex-1 overflow-y-auto">
        {filteredQuotations.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="FileText" size={48} className="text-text-muted mx-auto mb-4" />
            <p className="text-text-muted">
              {searchTerm || statusFilter !== 'all' ?'No se encontraron presupuestos' :'No hay presupuestos creados'
              }
            </p>
          </div>
        ) : (
          <div className="p-2">
            {filteredQuotations.map((quotation) => {
              const statusInfo = statusConfig[quotation.status];
              const isSelected = selectedQuotation?.id === quotation.id;
              
              return (
                <div
                  key={quotation.id}
                  onClick={() => onSelectQuotation(quotation)}
                  className={`p-4 rounded-lg border cursor-pointer transition-micro mb-2 ${
                    isSelected
                      ? 'border-primary bg-primary-50' :'border-border hover:border-primary-200 hover:bg-secondary-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-text-primary truncate">
                        {quotation.number}
                      </h3>
                      <p className="text-sm text-text-secondary truncate">
                        {quotation.clientName}
                      </p>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                      <div className="flex items-center space-x-1">
                        <Icon name={statusInfo.icon} size={12} />
                        <span>{statusInfo.label}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">
                      {formatDate(quotation.date)}
                    </span>
                    <span className="font-medium text-text-primary">
                      {formatCurrency(quotation.total)}
                    </span>
                  </div>
                  
                  {quotation.expiryDate && (
                    <div className="mt-2 text-xs text-text-muted">
                      Vence: {formatDate(quotation.expiryDate)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuotationList;